import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppointmentsService } from '../../api/AppointmentsAPI';
import { PaymentsService } from '../../api/PaymentsAPI';
import { CostsService } from '../../api/CostsAPI';
import { LOCATION_META } from '../../constants';
import { splitCents, apptCents } from '../../utils';
import s from '../../styles/dashboard.module.css';

const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);

// What each crew member takes from the tip. Split evenly, odd cents to the
// first share, matching the sidebar — see splitCents in utils.
function tipSplitText(tipCents, crewSize) {
  const heads = crewSize || 1;
  const shares = splitCents(tipCents, heads);
  const even = shares.every((v) => v === shares[0]);
  return heads === 1
    ? `${money(tipCents)} across 1`
    : `Locked at finish · ${money(shares[0])}${even ? '' : `/${money(shares[heads - 1])}`} each`;
}

// Mirrors the kind picker in the Log Cost modal.
const COST_KIND_LABEL = { contractor: 'Contractor', supply: 'Supply', other: 'Other' };

// "Contractor · Curb rash, two wheels · logged by Mark Santos"
const costMeta = (c) => [
  COST_KIND_LABEL[c.kind] || c.kind,
  c.note,
  c.enteredByName ? `logged by ${c.enteredByName}` : null,
].filter(Boolean).join(' \u00b7 ');

// Eyebrow wording per payment state, matching the badge on the card.
const HEAD_LABEL = {
  paid:     'PAID',
  invoiced: 'INVOICED',
  hotel:    'HOTEL A/R',
  comped:   'COMPED',
  ghost:    'GHOST',
};

function Row({ k, v, muted }) {
  return (
    <div className={s.drawerRow}>
      <span className={s.drawerRowKey}>{k}</span>
      <span className={`${s.drawerRowVal} ${muted ? s.drawerRowMuted : ''}`}>{v}</span>
    </div>
  );
}

function Section({ label, value, children }) {
  return (
    <div className={s.drawerCard}>
      <div className={s.drawerCardHead}>
        <span className={s.drawerCardLabel}>{label}</span>
        {value != null && <span className={s.drawerCardValue}>{value}</span>}
      </div>
      {children}
    </div>
  );
}

// What the person looking at this should actually do about it, in the wording
// the mockup uses for each state.
//
// Every figure named here is the quote BEFORE tax. `quotedCents` is the bare
// quote and `varianceCents` is a difference rather than a price, so neither
// moves with the header's before/after-tax switch — the sentence has to read
// the same whichever way that is set.
function nextStepText(d, crew = []) {
  if (!d) return null;

  if (!d.linked) {
    const near = d.candidates?.nearAmountCount || 0;
    const base = `${money(d.quotedCents)} is uncollected. Set how it was paid and it leaves the exception list either way.`;
    return near > 0
      ? `${base} There ${near === 1 ? 'is 1 unlinked payment' : `are ${near} unlinked payments`} nearby for about this amount — it may just need linking.`
      : base;
  }

  if (d.outstanding) {
    // A property remits on its own cycle; an invoice runs to a date.
    return d.billedTo
      ? `No action required. It clears when ${d.billedTo}'s next remittance is allocated.`
      : `No action required${d.invoiceNumber ? ` on invoice ${d.invoiceNumber}` : ''} until it falls due. If it is still unpaid then it moves to overdue and appears in Needs attention.`;
  }

  if (d.needsVerification) {
    // Cash is the sharper case: nothing in a payout will ever confirm it, and a
    // tip on it stays unpaid to the crew until the money is counted in.
    if (d.methodCode === 'cash') {
      const tip = d.tipCents
        ? ` Verify the ${money(d.quotedCents)} and the ${money(d.tipCents)} tip; the tip is credited to the crew but stays unpaid until the cash is counted in.`
        : ` Verify the ${money(d.quotedCents)} so it stops being an exception.`;
      return `Cash never touches Square, so nothing in a payout will ever confirm it.${tip}`;
    }
    return 'Recorded outside Square, so there is no bank record to check it against. Verifying confirms the money arrived; until then it stays in Needs attention.';
  }

  if (d.handling === 'needs_reason') {
    return 'Nothing to do. Revenue sits outside this visit, not on it, and the crew hours still count toward labour.';
  }

  // Paid, but not for what was quoted. Which way it went decides the sentence:
  // short is a decision to make, over is just a larger tip or a correction.
  if (d.varianceCents != null && d.varianceCents < 0) {
    return `The service revenue came in ${money(Math.abs(d.varianceCents))} under the quote. Decide whether the remainder is still owed or a discount, then the appointment closes.`;
  }

  if (d.tipCents) {
    const shares = splitCents(d.tipCents, crew.length || 1);
    const names = crew.length > 1
      ? `${crew.slice(0, -1).join(', ')} and ${crew[crew.length - 1]}`
      : crew[0];
    return names
      ? `Nothing to do. The tip split locked when the car was finished: ${money(shares[0])} each to ${names}.`
      : `Nothing to do. ${money(d.tipCents)} in tips is waiting on a crew assignment.`;
  }

  return 'Nothing to do. Collected and reconciled.';
}

export function AppointmentDrawer({ appt, staff = [], onClose, onTakePayment, onChangeMethod, onVerified }) {
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  // Close on Escape, like the other overlays.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const externalId = appt?.externalId || appt?.id;
  const { data: d, isLoading, isError, refetch } = useQuery({
    queryKey: ['appointment-payment', externalId],
    queryFn: () => AppointmentsService.getPaymentDetail(externalId),
    enabled: !!externalId,
  });

  const { data: costData } = useQuery({
    queryKey: ['appointment-costs', externalId],
    queryFn: () => CostsService.listForBooking(externalId),
    enabled: !!externalId,
  });
  const costs = costData?.costs || [];
  const costTotal = costs.reduce((n, c) => n + Number(c.amount || 0), 0);

  if (!appt) return null;

  const services = appt.services?.length ? appt.services : [appt.service].filter(Boolean);
  const crew = (appt.assignedStaff || [])
    .map(id => {
      const m = staff.find(x => x.externalId === id);
      if (!m) return null;
      return m.name || `${m.given_name || ''} ${m.family_name || ''}`.trim();
    })
    .filter(Boolean);

  const locName = LOCATION_META[appt.location]?.name || appt.location;
  const eyebrow = d?.linked
    ? (HEAD_LABEL[appt.payment?.status] || 'PAYMENT')
    : 'PAYMENT DUE';

  const bookedLine = [
    appt.bookedAtLabel || null,
    appt.bookedBy ? `by ${appt.bookedBy}` : null,
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={s.drawerScrim} onClick={onClose} />
      <aside className={s.drawer} role="dialog" aria-label={`${appt.customer} appointment`}>
        {/* The head is a flex row of two: the stack of lines, then the close
            button. The three lines have to share one child or they lay out
            side by side instead of under each other. */}
        <div className={s.drawerHead}>
          <div>
            <div className={s.drawerEyebrow}>{eyebrow} · {appt.time}</div>
            <div className={s.drawerTitle}>{appt.customer}</div>
            <div className={s.drawerSub}>{[locName, services.join(' + ')].filter(Boolean).join(' · ')}</div>
          </div>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.drawerBody}>
          {d && (
            <>
              <Section label="What happens next">
                <div className={s.drawerNote}>{nextStepText(d, crew)}</div>
              </Section>

              {verifyError && <div className={s.nextStepError}>{verifyError}</div>}

              <div className={s.drawerActions}>
                {!d.linked && (
                  <button className={s.drawerPrimary} onClick={() => onTakePayment?.(appt)}>
                    Take payment
                  </button>
                )}
                {d.linked && d.needsVerification && (
                  <button
                    className={s.drawerPrimary}
                    disabled={verifying}
                    onClick={async () => {
                      setVerifying(true);
                      setVerifyError('');
                      try {
                        await PaymentsService.verify(d.idPayment);
                        await refetch();
                        onVerified?.(appt);
                      } catch (err) {
                        setVerifyError(err.message || 'Could not verify this payment');
                      }
                      setVerifying(false);
                    }}
                  >
                    {verifying ? 'Verifying…' : 'Verify the money arrived'}
                  </button>
                )}
                <button className={s.drawerSecondary} onClick={() => onChangeMethod?.(appt)}>
                  Change how this is paid
                </button>
              </div>
            </>
          )}

          <Section label="Appointment" value={money(d?.quotedCents ?? apptCents(appt))}>
            <Row k="Booked" v={bookedLine || '—'} muted={!bookedLine} />
            <Row k="Services" v={services.join(', ') || '—'} muted={!services.length} />
            <Row k="Crew" v={crew.join(', ') || 'Unassigned'} muted={!crew.length} />
            <Row
              k="Tip split"
              v={d?.tipCents ? tipSplitText(d.tipCents, crew.length) : 'No tip'}
              muted={!d?.tipCents}
            />

            {/* Only when something has actually been logged — an empty costs
                block would read as a missing figure rather than a zero. */}
            {costs.length > 0 && (
              <div className={s.drawerCosts}>
                <div className={s.drawerCardHead}>
                  <span className={s.drawerCardLabel}>Costs</span>
                  <span className={s.drawerCardValue}>{money(costTotal)} logged</span>
                </div>

                {costs.map((c) => (
                  <div key={c.idCost} className={s.drawerCost}>
                    <div className={s.drawerCostTop}>
                      <span className={s.drawerCostName}>{c.payeeLabel || c.payeeName || 'Unnamed'}</span>
                      <span className={s.drawerCostAmt}>{money(c.amount)}</span>
                    </div>
                    <div className={s.drawerCostMeta}>{costMeta(c)}</div>
                  </div>
                ))}

                <div className={s.drawerCostTotals}>
                  <Row
                    k="Gross - collected"
                    v={money(d?.actualCents)}
                    muted={d?.actualCents == null}
                  />
                  <Row
                    k="Net of costs"
                    v={d?.actualCents == null ? '\u2014' : money(d.actualCents - costTotal)}
                    muted={d?.actualCents == null}
                  />
                </div>
              </div>
            )}
          </Section>

          {isLoading && <div className={s.drawerNote}>Loading payment…</div>}
          {isError && <div className={s.drawerNote}>Couldn’t load the payment for this appointment.</div>}

          {d && (
            <>
              <Section label="Payment" value={d.linked ? null : 'Nothing in'}>
                <Row k="Quoted revenue" v={money(d.quotedCents)} muted={d.quotedCents == null} />
                <Row k="Actual revenue" v={money(d.actualCents)} muted={d.actualCents == null} />
                <Row k="Tax" v={money(d.taxCents)} muted={d.taxCents == null} />
                <Row k="Tip" v={money(d.tipCents)} muted={d.tipCents == null} />
                <Row k="Total charged" v={money(d.totalChargedCents)} muted={d.totalChargedCents == null} />
                {d.varianceLabel && (
                  <div className={s.payVariance} style={{ marginTop: 8 }}>
                    {d.varianceLabel} — {d.reason || 'reason unknown'}
                  </div>
                )}
              </Section>

              <Section
                label="Reconciliation"
                value={!d.linked ? 'Unlinked'
                  : d.outstanding ? 'Outstanding'
                  : d.needsVerification ? 'Manual'
                  : 'Review'}
              >
                {d.linked ? (
                  <>
                    <Row k="Method" v={d.methodTitle || '—'} muted={!d.methodTitle} />
                    <Row
                      k="Source"
                      v={d.fromSquare
                        ? `Square${d.cardType ? ` · ${d.cardType}` : ''}`
                        : `Manual entry${d.enteredByName ? ` by ${d.enteredByName}` : ''}`}
                    />
                    {d.invoiceNumber && <Row k="Invoice" v={d.invoiceNumber} />}
                    {d.billedTo && <Row k="Billed to" v={d.billedTo} />}
                    {d.reason && <Row k="Reason" v={d.reason} />}
                    <Row
                      k="Verification"
                      v={d.verifiedAt
                        ? `${d.verifiedByName || 'Verified'} · ${new Date(d.verifiedAt).toLocaleString()}`
                        : d.fromSquare ? 'Square record is the proof'
                        : 'No Square record to check against'}
                      muted={!d.verifiedAt && d.fromSquare}
                    />
                    <Row
                      k="Settled"
                      v={d.settledAt ? new Date(d.settledAt).toLocaleString() : 'Not yet'}
                      muted={!d.settledAt}
                    />
                  </>
                ) : (
                  <>
                    <Row
                      k="Candidates"
                      v={d.candidates?.count
                        ? `${d.candidates.count} unlinked within ±${d.candidates.windowHours}h`
                        : `No payment within ${d.candidates?.windowHours ?? 6} hours`}
                      muted={!d.candidates?.count}
                    />
                    <Row
                      k="Amount"
                      v={(() => {
                        // Name the figure the search used — the quote with tax
                        // on it, which is what a payment should equal.
                        const target = d.candidates?.nearAmountCents ?? d.quotedCents;
                        return d.candidates?.nearAmountCount
                          ? `${d.candidates.nearAmountCount} near ${money(target)}`
                          : `No charge near ${money(target)}`;
                      })()}
                      muted={!d.candidates?.nearAmountCount}
                    />
                  </>
                )}
              </Section>

            </>
          )}
        </div>
      </aside>
    </>
  );
}
